import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api, Certification, InsurancePolicy, TaxDocument, maskTaxId } from '../../lib/api';
import { TradeBadge } from '../shared/TradeBadge';
import { ProfileCompletion } from './ProfileCompletion';
import { ShareProfileModal } from '../shared/ShareProfileModal';
import {
  User,
  MapPin,
  Phone,
  Mail,
  Briefcase,
  Award,
  CheckCircle2,
  Clock,
  Calendar,
  FileText,
  Share2
} from 'lucide-react';

export const ProfileView: React.FC = () => {
  const { profile, user, refreshProfile } = useAuth();
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [insurancePolicies, setInsurancePolicies] = useState<InsurancePolicy[]>([]);
  const [taxDoc, setTaxDoc] = useState<TaxDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    if (profile) {
      loadData();
    }
  }, [profile]);

  const loadData = async () => {
    if (!profile) return;

    try {
      const [certsData, insuranceData, taxData] = await Promise.all([
        api.get<Certification[]>('/certifications', { user_id: profile.id }),
        api.get<InsurancePolicy[]>('/insurance_policies', { user_id: profile.id }),
        api.get<TaxDocument[]>('/tax_documents', { user_id: profile.id }).catch(() => [])
      ]);

      setCertifications(certsData || []);
      setInsurancePolicies(insuranceData || []);
      setTaxDoc(taxData && taxData.length > 0 ? taxData[0] : null);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackFromCompletion = async () => {
    await refreshProfile();
    setShowProfileCompletion(false);
  };

  const getVerificationBadge = () => {
    switch (profile?.verification_status) {
      case 'verified':
        return (
          <div className="flex items-center space-x-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-full border border-green-200">
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-sm font-medium">Verified</span>
          </div>
        );
      case 'pending':
        return (
          <div className="flex items-center space-x-2 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full border border-amber-200">
            <Clock className="w-4 h-4" />
            <span className="text-sm font-medium">Pending</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center space-x-2 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full border border-slate-200">
            <span className="text-sm font-medium">Not Verified</span>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (showProfileCompletion) {
    return <ProfileCompletion onBack={handleBackFromCompletion} />;
  }

  return (
    <div className="space-y-6">
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 rounded-2xl p-8 overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="relative">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-white">Profile</h1>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowShareModal(true)}
                className="px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-lg hover:bg-white/20 transition-all flex items-center space-x-2"
              >
                <Share2 className="w-4 h-4" />
                <span>Share</span>
              </button>
              <button
                onClick={() => setShowProfileCompletion(true)}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg transition-all shadow-lg hover:shadow-xl"
              >
                Edit Profile
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-lg">
        <div className="h-32 bg-gradient-to-r from-blue-600 to-blue-800"></div>
        <div className="p-6 -mt-16">
          <div className="flex items-start space-x-6">
            <div className="w-32 h-32 rounded-xl bg-white border-4 border-white shadow-lg flex items-center justify-center">
              <User className="w-16 h-16 text-slate-400" />
            </div>

            <div className="flex-1 mt-16">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">{profile?.full_name}</h2>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(profile?.trades && profile.trades.length > 0) ? (
                      profile.trades.map((trade) => (
                        <TradeBadge key={trade} trade={trade} size="md" />
                      ))
                    ) : (
                      <p className="text-slate-600">No trades set</p>
                    )}
                  </div>
                </div>
                {getVerificationBadge()}
              </div>

              <div className="flex items-center space-x-6 mt-4 text-sm text-slate-600">
                <div className="flex items-center">
                  <Briefcase className="w-4 h-4 mr-2" />
                  {profile?.jobs_completed} jobs completed
                </div>
                <div className="flex items-center">
                  <Award className="w-4 h-4 mr-2" />
                  {profile?.rating.toFixed(1)} rating
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center">
              <User className="w-5 h-5 mr-2 text-blue-600" />
              About
            </h3>
            <p className="text-slate-600">
              {profile?.bio || 'No bio added yet. Click "Edit Profile" to add information about yourself.'}
            </p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-900 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                Tax & Compliance
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              <div className={`p-4 rounded-lg border-2 transition-all ${
                insurancePolicies.some(p => p.policy_type === 'liability')
                  ? 'bg-green-50 border-green-200'
                  : 'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-slate-700">General Liability</span>
                  {insurancePolicies.some(p => p.policy_type === 'liability') && (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  )}
                </div>
                {insurancePolicies.find(p => p.policy_type === 'liability') ? (
                  <div className="text-xs text-slate-600">
                    Exp: {new Date(insurancePolicies.find(p => p.policy_type === 'liability')!.expiry_date).toLocaleDateString()}
                  </div>
                ) : (
                  <div className="text-xs text-slate-500">Not provided</div>
                )}
              </div>

              <div className={`p-4 rounded-lg border-2 transition-all ${
                insurancePolicies.some(p => p.policy_type === 'workers_comp')
                  ? 'bg-green-50 border-green-200'
                  : 'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-slate-700">Workers Comp</span>
                  {insurancePolicies.some(p => p.policy_type === 'workers_comp') && (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  )}
                </div>
                {insurancePolicies.find(p => p.policy_type === 'workers_comp') ? (
                  <div className="text-xs text-slate-600">
                    Exp: {new Date(insurancePolicies.find(p => p.policy_type === 'workers_comp')!.expiry_date).toLocaleDateString()}
                  </div>
                ) : (
                  <div className="text-xs text-slate-500">Not provided</div>
                )}
              </div>

              <div className={`p-4 rounded-lg border-2 transition-all ${
                taxDoc?.w9_document_url
                  ? taxDoc.w9_verified
                    ? 'bg-green-50 border-green-200'
                    : 'bg-amber-50 border-amber-200'
                  : 'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-slate-700">W-9 Form</span>
                  {taxDoc?.w9_verified && (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  )}
                </div>
                {taxDoc?.w9_document_url ? (
                  <div className="text-xs text-slate-600">
                    {taxDoc.w9_verified ? 'Verified' : 'Pending verification'}
                  </div>
                ) : (
                  <div className="text-xs text-slate-500">Not uploaded</div>
                )}
              </div>

              <div className={`p-4 rounded-lg border-2 transition-all ${
                taxDoc?.tax_id
                  ? 'bg-green-50 border-green-200'
                  : 'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-slate-700">Tax ID</span>
                  {taxDoc?.tax_id && (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  )}
                </div>
                {taxDoc?.tax_id ? (
                  <div className="text-xs text-slate-600 font-mono">
                    {maskTaxId(taxDoc.tax_id)}
                  </div>
                ) : (
                  <div className="text-xs text-slate-500">Not provided</div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-900 flex items-center">
                <Award className="w-5 h-5 mr-2 text-blue-600" />
                Certifications & Licenses
              </h3>
            </div>

            {certifications.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-sm">No certifications added yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {certifications.map((cert) => (
                  <div key={cert.id} className="flex items-start justify-between p-4 bg-gradient-to-br from-slate-50 to-blue-50 rounded-lg hover:shadow-md transition-all">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="font-medium text-slate-900">{cert.name}</h4>
                        {cert.verified && (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                      {cert.issuer && (
                        <p className="text-sm text-slate-600">{cert.issuer}</p>
                      )}
                      {cert.expiry_date && (
                        <div className="flex items-center text-xs text-slate-500 mt-1">
                          <Calendar className="w-3 h-3 mr-1" />
                          Expires {new Date(cert.expiry_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full capitalize ${
                      cert.type === 'license'
                        ? 'bg-blue-100 text-blue-700'
                        : cert.type === 'certification'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-slate-100 text-slate-700'
                    }`}>
                      {cert.type}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center">
              <Mail className="w-5 h-5 mr-2 text-blue-600" />
              Contact Information
            </h3>
            <div className="space-y-3">
              <div className="flex items-start">
                <Mail className="w-4 h-4 text-slate-400 mt-1 mr-3" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-500 mb-1">Email</div>
                  <div className="text-sm text-slate-900 truncate">{user?.email}</div>
                </div>
              </div>

              <div className="flex items-start">
                <Phone className="w-4 h-4 text-slate-400 mt-1 mr-3" />
                <div className="flex-1">
                  <div className="text-xs text-slate-500 mb-1">Phone</div>
                  <div className="text-sm text-slate-900">{profile?.phone || 'Not provided'}</div>
                </div>
              </div>

              <div className="flex items-start">
                <MapPin className="w-4 h-4 text-slate-400 mt-1 mr-3" />
                <div className="flex-1">
                  <div className="text-xs text-slate-500 mb-1">Location</div>
                  <div className="text-sm text-slate-900">{profile?.location || 'Not provided'}</div>
                </div>
              </div>

              <div className="flex items-start">
                <Briefcase className="w-4 h-4 text-slate-400 mt-1 mr-3" />
                <div className="flex-1">
                  <div className="text-xs text-slate-500 mb-1">Company</div>
                  <div className="text-sm text-slate-900">{profile?.company_name || 'Not provided'}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 rounded-xl p-6 text-white overflow-hidden shadow-lg">
            <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl"></div>
            <div className="relative">
              <h3 className="font-bold mb-2">Get Verified</h3>
              <p className="text-sm text-slate-300 mb-4">
                Boost your profile visibility and unlock more opportunities
              </p>
              <button
                onClick={() => setShowProfileCompletion(true)}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium py-2 rounded-lg transition-all shadow-md hover:shadow-lg"
              >
                Start Verification
              </button>
            </div>
          </div>
        </div>
      </div>

      <ShareProfileModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        profile={profile}
        certifications={certifications}
        insurancePolicies={insurancePolicies}
        email={user?.email}
      />
    </div>
  );
};
